import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { Button, Grid, Typography } from '@material-ui/core'
import Decimal from 'decimal.js'
import { useFormik } from 'formik'
import { DECIMAL_PLACES, DECIMAL_PLACES_PERCENTAGE } from 'constants/calcConstants'
import { bankRoundingDecimal, hasMoreDecimalPlaces } from 'utils/rounding'
import SwitchExtended from 'UI/Switch/SwitchExtended'
import SplitIsoCommissionInput from './SplitIsoCommissionInput'

interface Props {
  initialValues: { [key: number]: Decimal }
  name: string
  selectedIsos: Array<{ id: number; name: string }>
  isoFeeAmount: number | undefined
  disabled: boolean | undefined
  propsSetFieldValue: (name: string, data: any[]) => void
  handleClosePopover: () => void
  classes: any
}

interface ISplitIsoCommissionForm {
  [key: number]: IIsoCommision
}

interface IIsoSplitCommisionValidation {
  validate: (data: Array<IIsoCommision>, amount: number) => {}
  error: string
}

export interface IIsoCommision {
  percentage: string
  amount: string
}

export enum SplitIsoCommisionCalcultationMode {
  Percentage,
  Amount,
}

const validations: IIsoSplitCommisionValidation[] = [
  {
    validate: (data: Array<IIsoCommision>, isoFeeAmount: number) => {
      const sum = data.reduce((acc, el) => acc.plus(new Decimal(el.amount)), new Decimal(0))
      return !sum.greaterThan(isoFeeAmount)
    },
    error: 'Total split amount cannot be higher than Iso Fee.',
  },
  {
    validate: (data: Array<IIsoCommision>, isoFeeAmount: number) => {
      const sum = data.reduce((acc, el) => acc.plus(new Decimal(el.amount)), new Decimal(0))
      return !sum.lessThan(isoFeeAmount)
    },
    error: 'Total split amount cannot be lower than Iso Fee.',
  },
]

export default function SplitIsoCommissionForm({
  initialValues,
  selectedIsos,
  isoFeeAmount,
  propsSetFieldValue,
  handleClosePopover,
  name,
  disabled,
  ...props
}: Props): ReactElement {
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<SplitIsoCommisionCalcultationMode>(
    SplitIsoCommisionCalcultationMode.Amount
  )
  let initialFormValue: ISplitIsoCommissionForm = {}

  const calculateComissionAmount = useCallback(
    (percentage: Decimal): Decimal => {
      const calculatedAmount = new Decimal(isoFeeAmount ?? 0)
        .times(percentage)
        .div(100)
        .toDecimalPlaces(DECIMAL_PLACES)

      return calculatedAmount
    },
    [isoFeeAmount]
  )

  const total = new Decimal(isoFeeAmount ?? 0)
  for (const [key, value] of Object.entries(initialValues)) {
    const numberKey = Number(key)
    const amount = new Decimal(value)
    const percentage = total.isZero() ? new Decimal(0) : amount.dividedBy(total).times(100)

    initialFormValue = {
      ...initialFormValue,
      [numberKey]: {
        percentage: percentage.toString(),
        amount: amount.toString(),
      },
    }
  }

  const { values, handleSubmit, setFieldValue, setValues } = useFormik({
    initialValues: initialFormValue,
    enableReinitialize: false,
    onSubmit: formValues => {
      const commisions: Array<IIsoCommision> = Object.values(formValues)

      for (const validation of validations) {
        if (!validation.validate(commisions, isoFeeAmount ?? 0)) {
          return setError(validation.error)
        }
      }

      const result: Array<{ id: number; value: Decimal }> = []
      for (const [key, value] of Object.entries(formValues)) {
        const numberKey = Number(key)

        result.push({ id: numberKey, value: new Decimal(value.amount) })
      }

      propsSetFieldValue(name, result)
      handleClosePopover()
    },
  })

  useEffect(() => {
    if (mode !== SplitIsoCommisionCalcultationMode.Percentage) return

    const updatedValues: ISplitIsoCommissionForm = {}

    Object.entries(values).forEach(([key, value]) => {
      const entryKey = Number(key)

      // Add null check to prevent accessing properties on undefined
      if (!value || typeof value !== 'object' || !value.percentage) {
        return
      }

      const hasMoreThanThreeDecimals = hasMoreDecimalPlaces(
        value.percentage,
        DECIMAL_PLACES_PERCENTAGE
      )

      if (!hasMoreThanThreeDecimals) {
        updatedValues[entryKey] = value
        return
      }

      const roundedPercentage = bankRoundingDecimal(value.percentage, DECIMAL_PLACES_PERCENTAGE)
      updatedValues[entryKey] = {
        percentage: roundedPercentage.toString(),
        amount: calculateComissionAmount(roundedPercentage).toString(),
      }
    })

    setValues(updatedValues)
  }, [mode])

  return (
    <form onSubmit={handleSubmit}>
      {mode === SplitIsoCommisionCalcultationMode.Amount ? (
        <SwitchExtended
          key={'amount'}
          variants={[
            { title: 'Amount', value: 'amount' },
            { title: 'Percentage', value: 'percentage' },
          ]}
          value="amount"
          onClick={() => {
            setMode(SplitIsoCommisionCalcultationMode.Percentage)
          }}
        />
      ) : (
        <SwitchExtended
          key={'percentage'}
          variants={[
            { title: 'Amount', value: 'amount' },
            { title: 'Percentage', value: 'percentage' },
          ]}
          value="percentage"
          onClick={() => {
            setMode(SplitIsoCommisionCalcultationMode.Amount)
          }}
        />
      )}
      <Grid container spacing={4} style={{ marginTop: '2rem' }}>
        {selectedIsos.map(({ id, name: isoName }) => {
          const currentCommision = values[id] || {
            percentage: '0',
            amount: '0',
          }

          return (
            <Grid item xs={12} key={id} style={{ marginBottom: '1.5rem' }}>
              <SplitIsoCommissionInput
                {...props}
                commision={currentCommision}
                disabled={disabled}
                mode={mode}
                isoName={isoName}
                isoId={id}
                isoFeeAmount={isoFeeAmount}
                onChange={(changedId, commision) => setFieldValue(changedId.toString(), commision)}
              />
            </Grid>
          )
        })}
      </Grid>

      {error && (
        <Typography gutterBottom color="error" style={{ marginBottom: '1rem' }}>
          {error}
        </Typography>
      )}
      <Button
        type="submit"
        color="primary"
        variant="contained"
        fullWidth
        style={{ marginTop: '0.5rem' }}
      >
        Split
      </Button>
    </form>
  )
}
